import AltRoute from '@mui/icons-material/AltRoute';
import Anchor from '@mui/icons-material/Anchor';
import CreateOutlined from '@mui/icons-material/CreateOutlined';
import CrisisAlertOutlined from '@mui/icons-material/CrisisAlertOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DirectionsBoatOutlined from '@mui/icons-material/DirectionsBoatOutlined';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined';
import Fence from '@mui/icons-material/Fence';
import FmdBadOutlined from '@mui/icons-material/FmdBadOutlined';
import HandymanOutlined from '@mui/icons-material/HandymanOutlined';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import History from '@mui/icons-material/History';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import MapOutlined from '@mui/icons-material/MapOutlined';
import Navigation from '@mui/icons-material/Navigation';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import SatelliteAltOutlined from '@mui/icons-material/SatelliteAltOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import TravelExplore from '@mui/icons-material/TravelExplore';
import Water from '@mui/icons-material/Water';
import SvgIcon from '@mui/material/SvgIcon';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum EMenuItems {
  NOTHING_SELECTED = '',
  CITIES = 'Cities',
  COUNTRIES = 'Countries',
  MAPS = 'Maps & Charts',
  CAPELLA = 'SAR Imagery',
  FLEETS = 'Fleets',
  ALERTS = 'Fleet Notifications',
  MARITIME_INCIDENTS = 'Maritime Incidents',
  INCIDENTS = 'Incidents',
  BOUNDARIES = 'Boundaries',
  PORTS = 'Ports',
  ROUTES = 'Routes',
  DRAWINGS = 'Drawings',
  RI_MARITIME_AREAS = 'Risk Intelligence Maritime Areas',
  HISTORY = 'History',
  DOCUMENTS = 'Documents',
  SAVE_SETUP = 'Save Setup',
  DATA_POLICY = 'Data Policy',
  FEEDBACK = 'Feedback & Support',
  TOOLS = 'Tools',
  NEARBY_VESSELS = 'Nearby Vessels',
  AVCS = 'AVCS Online',
  ESA = 'ESA Sentinel 2',
  USER_PREFERENCES = 'Settings',
  SIGN_OUT = 'Sign Out',
}

export enum EDossiers {
  NOTHING = '',
  CITY = 'City',
  COUNTRY = 'Country',
  INCIDENT = 'Incident',
  MARITIME_INCIDENT = 'Maritime Incident',
  PORT = 'Port',
  DRAWING = 'Drawing',
  RI_MARITIME_AREA = 'RI Maritime Area',
  ROUTE = 'Route',
  VESSEL = 'Vessel',
}

export enum EMenuIcons {
  MAPS = 'MapOutlined',
  CAPELLA = 'SatelliteAltOutlined',
  FLEETS = 'DirectionsBoatOutlined',
  ALERTS = 'NotificationsOutlined',
  INCIDENTS = 'FmdBadOutlined',
  MARITIME_INCIDENTS = 'CrisisAlertOutlined',
  BOUNDARIES = 'Fence',
  PORTS = 'Anchor',
  ROUTES = 'AltRoute',
  DRAWINGS = 'CreateOutlined',
  RI_MARITIME_AREAS = 'Water',
  HISTORY = 'History',
  DOCUMENTS = 'DescriptionOutlined',
  SAVE_SETUP = 'SaveOutlined',
  DATA_POLICY = 'LockOutlined',
  FEEDBACK = 'FeedbackOutlined',
  HELP_PAGES = 'HelpOutlineOutlined',
  TOOLS = 'HandymanOutlined',
  AVCS = 'ExploreOutlined',
  ESA = 'TravelExplore',
  GLOBAL_AIS = 'Navigation',
  USER_PREFERENCES = 'SettingsOutlined',
  SIGN_OUT = 'LogoutOutlined',
}

export const MenuIcons: Record<EMenuIcons, typeof SvgIcon> = {
  MapOutlined,
  DirectionsBoatOutlined,
  NotificationsOutlined,
  FmdBadOutlined,
  Fence,
  Anchor,
  AltRoute,
  CreateOutlined,
  Water,
  History,
  SaveOutlined,
  LockOutlined,
  FeedbackOutlined,
  HelpOutlineOutlined,
  DescriptionOutlined,
  HandymanOutlined,
  SatelliteAltOutlined,
  ExploreOutlined,
  TravelExplore,
  Navigation,
  CrisisAlertOutlined,
  SettingsOutlined,
  LogoutOutlined,
};

export const EMenuTitles = {
  ALERTS_AND_INCIDENTS: 'Notifications & Incidents',
  MY_FLEET: 'My Fleet',
  AREAS: 'Areas',
  OBSERVATIONS: 'Observations',
  MAP_AND_TOOLS: 'Maps & Tools',
  USER: 'User',
};

interface MenuState {
  menuOpen: boolean;
  selectedOption: EMenuItems;
  secondaryMenuOpen: boolean;
  selectedDossier: EDossiers;
  dossierHistory: { dossier: EDossiers; option: EMenuItems }[];
  securityRiskAssessmentOpen: boolean;
  threatAssessmentOpen: boolean;
  vulnerabilityAssessmentOpen: boolean;
  selectedAreaDocumentId: string;
  timelineMinimised: boolean;
}

export const INITIAL_MENU_STATE: MenuState = {
  menuOpen: false,
  selectedOption: EMenuItems.NOTHING_SELECTED,
  dossierHistory: [],
  secondaryMenuOpen: false,
  selectedDossier: EDossiers.NOTHING,
  securityRiskAssessmentOpen: false,
  threatAssessmentOpen: false,
  vulnerabilityAssessmentOpen: false,
  selectedAreaDocumentId: '',
  timelineMinimised: false,
};

const MenuSlice = createSlice({
  name: 'menu',
  initialState: INITIAL_MENU_STATE,
  reducers: {
    toggleMainMenu: (state) => {
      state.menuOpen = !state.menuOpen;
    },
    closeMainMenu: (state) => {
      state.menuOpen = false;
    },
    closeMenus: (state) => {
      state.menuOpen = false;
      state.secondaryMenuOpen = false;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    toggleSecondaryMenu: (state, action) => {
      if (action.payload) {
        state.secondaryMenuOpen = action.payload;
        state.menuOpen = false;
      } else {
        state.secondaryMenuOpen = action.payload;
      }
    },
    setSecondaryMenu: (state) => {
      state.secondaryMenuOpen = !state.secondaryMenuOpen;
    },
    setAllDossierClosed: (state) => {
      state.selectedDossier = EDossiers.NOTHING;
      state.dossierHistory = [];
      state.threatAssessmentOpen = false;
      state.securityRiskAssessmentOpen = false;
      state.vulnerabilityAssessmentOpen = false;
    },
    setAllAssessmentsClosed: (state) => {
      state.threatAssessmentOpen = false;
      state.securityRiskAssessmentOpen = false;
      state.vulnerabilityAssessmentOpen = false;
    },
    setSelectedOption: (state, action: PayloadAction<EMenuItems>) => {
      MenuSlice.caseReducers.setAllAssessmentsClosed(state);
      state.secondaryMenuOpen = true;
      state.selectedOption = action.payload;
    },
    setSelectedDossier: (state, action: PayloadAction<EDossiers>) => {
      if (state.selectedDossier === action.payload) {
        // if we're already on this dossier don't do anything
        return;
      }
      state.dossierHistory = [
        ...state.dossierHistory,
        { dossier: state.selectedDossier, option: state.selectedOption },
      ];
      state.selectedDossier = action.payload;
    },
    setSelectedAreaDocument: (state, action: PayloadAction<string>) => {
      state.selectedAreaDocumentId = action.payload;
    },
    setThreatAssessmentOpen: (state, action: PayloadAction<boolean>) => {
      MenuSlice.caseReducers.setAllDossierClosed(state);
      state.threatAssessmentOpen = action.payload;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    setSecurityRiskAssessmentOpen: (state, action: PayloadAction<boolean>) => {
      MenuSlice.caseReducers.setAllDossierClosed(state);
      state.securityRiskAssessmentOpen = action.payload;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    setVulnerabilityAssessmentOpen: (state, action: PayloadAction<boolean>) => {
      MenuSlice.caseReducers.setAllDossierClosed(state);
      state.vulnerabilityAssessmentOpen = action.payload;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    setTimelineMinimised: (state, action: PayloadAction<boolean>) => {
      state.timelineMinimised = action.payload;
    },
  },
});

export const {
  toggleMainMenu,
  closeMenus,
  closeMainMenu,
  toggleSecondaryMenu,
  setSecondaryMenu,
  setThreatAssessmentOpen,
  setSecurityRiskAssessmentOpen,
  setVulnerabilityAssessmentOpen,
  setSelectedOption,
  setSelectedDossier,
  setAllDossierClosed,
  setAllAssessmentsClosed,
  setSelectedAreaDocument,
  setTimelineMinimised,
} = MenuSlice.actions;

export default MenuSlice.reducer;
